import { FC, createContext, useContext, useState, useCallback } from 'react';

import {
  useCompleteInitialConfig,
  useParseLocationQuery,
  useRequest,
  useInterval,
  useImportsOrdersDialogsState,
  useToggle,
} from '~hooks/index';
import { getDashboardCarrierStatitics } from '~services/dashboard';
import { DashboardCarrierStatitics } from '~services/dashboard/types';

import {
  DashboardVariantV2CarrierQueryParams,
  DashboardVariantV2CarrierContextValue,
  DashboardVariantV2CarrierContextTabId,
} from './types';

const DEFAULT_STATISTICS: DashboardCarrierStatitics = {
  companyInfo: {
    quantityVehicles: 0,
    quantityWarehouses: 0,
    quantityCarrierCompanies: 0,
    quantityGiverCompanies: 0,
  },
  totalItems: 0,
  collect: {
    totalCollectedItems: 0,
    totalCollectItems: 0,
    totalToCollectItems: 0,
  },
  inWarehouse: {
    totalDelayedInWarehouseItems: 0,
    totalInWarehouseItems: 0,
    totalNextDaysInWarehouseItems: 0,
    totalTodayInWarehouseItems: 0,
  },
  inProgress: {
    totalInProgressItems: 0,
    totalInProgressItemsFlex: 0,
    totalInProgressItemsNoFlex: 0,
    totalItemsInProgress: 0,
    totalItemsScheduled: 0,
  },
  completed: {
    totalCompletedtems: 0,
    totalWithIssuesItems: 0,
    totalWithoutIssuesItems: 0,
  },
  route: {
    totalCompletedRoutes: 0,
    totalInProgressRoutes: 0,
    totalPendingRoutes: 0,
    totalRoutes: 0,
  },
};

const DEFAULT_INTERVAL_RELOAD_STATISTICS = 15000; //@TODO: UI V2: Verify

const DashboardVariantV2CarrierContext =
  createContext<DashboardVariantV2CarrierContextValue>({
    statistics: {
      loading: true,
      data: DEFAULT_STATISTICS,
      reloadData: () => {},
    },
    activeTab: {
      currentTab: 'inTransit',
      setActiveTab: () => {},
      getIsActiveTab: () => false,
    },
    dialogs: {
      importFromFile: { open: false, toggleOpen: () => {} },
      importFromIntegration: { open: false, toggleOpen: () => {} },
      importFromAssociatedCompany: { open: false, toggleOpen: () => {} },
      updateGeoError: { open: false, toggleOpen: () => {} },
    },
    ignoreRefreshBySocket: {
      value: false,
      toggleValue: () => {},
    },
    expandedHasChangeOrdersAlert: {
      value: false,
      toggleValue: () => {},
    },
    modifiedItemsIdBySocket: {
      value: [],
      setValue: () => {},
    },
  });

export const useDashboardVariantV2CarrierContext =
  (): DashboardVariantV2CarrierContextValue =>
    useContext(DashboardVariantV2CarrierContext);

const DashboardVariantV2CarrierContextProvider: FC = ({ children }) => {
  const { setCompletedConfig, handleOpenMenuFirstStepOnCompleted } =
    useCompleteInitialConfig();

  const queryParams =
    useParseLocationQuery<DashboardVariantV2CarrierQueryParams>();

  const [activeTab, setActiveTab] =
    useState<DashboardVariantV2CarrierContextTabId>('inTransit');

  const getIsActiveTab = useCallback(
    (currentTab: DashboardVariantV2CarrierContextTabId) =>
      activeTab === currentTab,
    [activeTab],
  );

  const [statistics, setStatistics] =
    useState<DashboardCarrierStatitics>(DEFAULT_STATISTICS);
  const [loadingStatics, setLoadingStatics] = useState(true);
  const [isFirstRequestStatistics, setIsFirstRequestStatistics] =
    useState(true);
  const [, , , reloadStatistics] = useRequest(
    {
      request: getDashboardCarrierStatitics,
      payload: null,
      withPostSuccess: (response) => {
        const currentStatistics = response.data
          ?.data as DashboardCarrierStatitics;

        setStatistics(currentStatistics);

        if (
          isFirstRequestStatistics &&
          currentStatistics.route.totalInProgressRoutes === 0
        ) {
          setActiveTab('inWarehouse');
        }

        handleOpenMenuFirstStepOnCompleted();
      },
      withPostFetch: () => {
        setLoadingStatics(false);
        setIsFirstRequestStatistics(false);
      },
    },
    [],
  );

  const reloadDataByConfig = useCallback(
    (isCompleteConfig: boolean) => {
      setCompletedConfig(isCompleteConfig);
      reloadStatistics(null);
    },
    [setCompletedConfig, reloadStatistics],
  );

  useInterval(
    () => reloadDataByConfig(false),
    DEFAULT_INTERVAL_RELOAD_STATISTICS,
  );

  const {
    importFromFile: dialogImportFromFileToggleState,
    importFromIntegration: dialogImportFromIntegrationToggleState,
    importFromAssociatedCompany: dialogImportFromAssociatedCompanyToggleState,
  } = useImportsOrdersDialogsState({
    defaultOpenDialogImportFromFile: queryParams?.openImporterFromFile,
    defaultOpenDialogImportFromIntegration:
      queryParams?.openImporterFromIntegration,
    defaultOpenImportFromAssociatedCompany:
      queryParams?.openImporterFromAssociatedCompany,
  });

  const [openDialogUpdateGeoError, toogleOpenDialogUpdateGeoError] =
    useToggle(false);

  const [ignoreRefreshBySocket, toggleIgnoreRefreshBySocket] = useToggle(false);

  const [expandedHasChangeOrdersAlert, toggleExpandedHasChangeOrdersAlert] =
    useToggle(false);

  const [modifiedItemsIdBySocket, setModifiedItemsIdBySocket] = useState<
    string[]
  >([]);

  return (
    <DashboardVariantV2CarrierContext.Provider
      value={{
        statistics: {
          loading: loadingStatics,
          data: statistics,
          reloadData: reloadDataByConfig,
        },
        activeTab: {
          currentTab: activeTab,
          setActiveTab,
          getIsActiveTab,
        },
        dialogs: {
          importFromFile: dialogImportFromFileToggleState,
          importFromIntegration: dialogImportFromIntegrationToggleState,
          importFromAssociatedCompany:
            dialogImportFromAssociatedCompanyToggleState,
          updateGeoError: {
            open: openDialogUpdateGeoError,
            toggleOpen: toogleOpenDialogUpdateGeoError,
          },
        },
        ignoreRefreshBySocket: {
          value: ignoreRefreshBySocket,
          toggleValue: toggleIgnoreRefreshBySocket,
        },
        expandedHasChangeOrdersAlert: {
          value: expandedHasChangeOrdersAlert,
          toggleValue: toggleExpandedHasChangeOrdersAlert,
        },
        modifiedItemsIdBySocket: {
          value: modifiedItemsIdBySocket,
          setValue: setModifiedItemsIdBySocket,
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContext.Provider>
  );
};

export default DashboardVariantV2CarrierContextProvider;
